import {
  AssetType,
  FireTVCampaignAutomationClient,
  PresignedUrlAction,
  Placement,
} from '@amzn/fire-tv-campaign-automation-service';
import { PLACEHOLDER_URLS, SCREEN_SAVER_PLACEHOLDER_URLS } from 'src/constants';
import { AssetContextState } from 'src/contexts/AssetContext';
import { AssetStatus, PreviewAssets } from 'src/types';

import { getPresignedUrls } from '../clients/controlPlane/apiMethods';

export interface PreviewAssetStates {
  assets: AssetContextState;
  bodyCopy?: string;
}

export interface PreviewChanges {
  assets: AssetType[];
  hasBodyCopyChanged: boolean;
}

const LOADING_PREVIEW_ASSET_STATES: AssetStatus[] = [AssetStatus.TRANSFORMATION_IN_PROGRESS];

// Placement-specific placeholder mapping
export const PLACEMENT_PLACEHOLDER_URLS: Partial<Record<Placement, PreviewAssets>> = {
  [Placement.SCREEN_SAVER]: {
    [AssetType.BACKGROUND_IMAGE]: SCREEN_SAVER_PLACEHOLDER_URLS[AssetType.BACKGROUND_IMAGE],
    [AssetType.LOGO]: PLACEHOLDER_URLS[AssetType.LOGO],
  },
  [Placement.FEATURE_ROTATOR]: {
    [AssetType.BACKGROUND_IMAGE]: PLACEHOLDER_URLS[AssetType.BACKGROUND_IMAGE],
    [AssetType.LOGO]: PLACEHOLDER_URLS[AssetType.LOGO],
    [AssetType.VIDEO_TRAILER]: PLACEHOLDER_URLS[AssetType.VIDEO_TRAILER],
  },
};

/**
 * Get asset urls for the assets and the keys provided.
 * If key is not provided - returns back placeholder urls
 * else - gets the presigned urls for the keys
 * @param assets - Array of assets with the type and key provided
 * @param controlPlaneClient- Client instance for making API calls
 * @param placement - Placement type for selecting appropriate placeholder URLs
 * @returns Promise that resolves when URLs are fetched for the keys provided.
 */
export const getAssetUrls = async (
  assets: { type: AssetType; key: string | undefined }[],
  controlPlaneClient: FireTVCampaignAutomationClient,
  placement: Placement,
): Promise<Record<string, string>> => {
  const assetsWithKeys = assets.filter((asset) => asset.key);
  const assetsWithoutKeys = assets.filter((asset) => !asset.key);

  const placeholderMap = PLACEMENT_PLACEHOLDER_URLS[placement] || PLACEHOLDER_URLS;

  const placeholderUrls = Object.fromEntries(
    assetsWithoutKeys.map((asset) => [asset.type, placeholderMap[asset.type] as string]),
  );

  const presignedUrls = await getPresignedUrlsForAssets(
    assetsWithKeys.map((asset) => ({ type: asset.type, key: asset.key! })),
    controlPlaneClient,
  );

  return { ...placeholderUrls, ...presignedUrls };
};

/**
 * Gets presigned URLs for assets with keys via batched API call
 * @param assetsWithKeys - Array of assets with their types and keys
 * @param controlPlaneClient - Client instance for making API calls
 * @returns Promise that resolves when URLs are updated
 */
export const getPresignedUrlsForAssets = async (
  assetsWithKeys: { type: AssetType; key: string }[],
  controlPlaneClient: FireTVCampaignAutomationClient,
): Promise<Record<string, string>> => {
  if (assetsWithKeys.length === 0) {
    return {};
  }
  const keys = assetsWithKeys.map((item) => item.key);
  const presignedUrlResponses = await getPresignedUrls(
    controlPlaneClient,
    PresignedUrlAction.GET,
    keys,
  );
  const presignedUrlMap = new Map(
    presignedUrlResponses.map((response) => [response.key, response.presignedUrl]),
  );

  // Map asset types to their presigned URLs
  return Object.fromEntries(
    assetsWithKeys.map((asset) => [
      asset.type,
      presignedUrlMap.get(asset.key) || PLACEHOLDER_URLS[asset.type],
    ]),
  );
};

/**
 * Get changed asset types by comparing current and previous asset states
 * @param current - Current preview asset states (assets + bodyCopy)
 * @param previous - Previous preview asset states (assets + bodyCopy)
 * @returns PreviewChanges object with changed assets and body copy flag
 */
export function getPreviewChanges(
  current: PreviewAssetStates,
  previous: PreviewAssetStates,
): PreviewChanges {
  const changedAssets = Object.keys(current.assets).filter(
    (assetType) =>
      current.assets[assetType as AssetType]?.key !== previous.assets[assetType as AssetType]?.key,
  ) as AssetType[];

  return {
    assets: changedAssets,
    hasBodyCopyChanged: current.bodyCopy !== previous.bodyCopy,
  };
}

/**
 * Utility to check if any assets are in loading states
 * @param assetStates - Current state of all assets
 * @returns Boolean indicating if any assets are in loading states
 * @description This is used to determine if we should show the loading spinner or not.
 * We should show the loading spinner if any asset is in loading state.
 * We should not show the loading spinner if there are no assets at all.
 * We should not show the loading spinner if there are no assets in loading state.
 */
export function areAnyAssetsInLoadingStates(assetStates: AssetContextState): boolean {
  return Object.values(assetStates).some(
    (asset) => asset.status && LOADING_PREVIEW_ASSET_STATES.includes(asset.status),
  );
}
